import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Card, CardContent } from '@/shared/ui/card';
import { RedeemError, RedeemErrorCode } from '@/lib/api/routingApi';
import { useRedeemAccessKey } from '@/hooks/useRoutingUserDetails';
import { useAuth, type AuthState } from '@/hooks/useAuth';

export default function AccessKeyPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { logout }: AuthState = useAuth();
  const { mutateAsync: redeem, isPending } = useRedeemAccessKey();

  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const disabled: boolean = isPending || !code.trim();

  const handleSubmit = async (): Promise<void> => {
    if (disabled) return;

    setError(null);

    try {
      await redeem(code.trim());
    } catch (error) {
      if (error instanceof RedeemError) {
        switch (error.errorCode) {
          case RedeemErrorCode.KeyInvalid:
            setError(t('accessKey.errorInvalid'));
            break;
          case RedeemErrorCode.KeyDeactivated:
            setError(t('accessKey.errorDeactivated'));
            break;
          case RedeemErrorCode.KeyExhausted:
            setError(t('accessKey.errorNoUses'));
            break;
          case RedeemErrorCode.AlreadyPro:
            setError(t('accessKey.errorAlreadyPro'));
            break;
          case RedeemErrorCode.Unknown:
            setError(t('accessKey.errorUnknown'));
            break;
          case RedeemErrorCode.KeyEmpty:
            setError(t('accessKey.errorKeyEmpty'));
            break;
          default:
            setError(t('accessKey.errorGeneral'));
        }
      } else {
        setError(t('accessKey.errorGeneral'));
      }
    }
  };

  const handleFormSubmit = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    void handleSubmit();
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCode(event.target.value.toUpperCase());
  };

  const handleLogout = (): void => {
    void logout();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4 py-12 overflow-y-auto">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-foreground mb-8">
          {t('accessKey.title')}
        </h1>

        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground text-center mb-6">
              {t('accessKey.subtitle')}
            </p>

            {error && (
              <div
                role="alert"
                className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive text-center"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <Input
                value={code}
                onChange={handleCodeChange}
                placeholder={t('accessKey.placeholder')}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                className="text-center tracking-widest h-11 text-base"
              />

              <Button type="submit" disabled={disabled} className="w-full h-11">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('accessKey.redeemButton')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 flex flex-col items-center gap-2 text-sm text-muted-foreground">
          <p className="text-center">
            {t('accessKey.contactHintPrefix')}
            <a
              href={`tel:${t('accessKey.contactPhone').replace(/\s/g, '')}`}
              className="text-primary hover:underline"
            >
              {t('accessKey.contactPhone')}
            </a>
            {t('accessKey.contactHintSuffix')}
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="font-medium hover:text-foreground"
          >
            {t('accessKey.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
